import { createAuthenticatedFetch } from "@assetwatch/auth";
import type { AppType } from "@assetwatch/enterprise-backend/app";
import {
  chatConversationHistoryResponseSchema,
  chatConversationListResponseSchema,
  chatResponseSchema,
  type EnterpriseActiveClarification,
  type EnterpriseChatConversationHistoryResponse,
  type EnterpriseChatConversationListResponse,
  type EnterpriseChatConversationSummary,
  type EnterpriseChatHistoryMessage,
  type EnterpriseClarificationSelection,
  type EnterprisePredefinedQuestionId,
} from "@assetwatch/enterprise-backend/chat/schemas";
import { hc } from "hono/client";
import type { output as ZodOutput, ZodTypeAny } from "zod";
import i18n from "../i18n/config";
import {
  addEnterpriseApiBreadcrumb,
  captureAskReliError,
  createEnterpriseRequestId,
} from "../lib/sentry";
export class EnterpriseApiError extends Error {
  readonly status: number;
  readonly operation?: string;

  constructor(
    message: string,
    status: number,
    options?: { operation?: string },
  ) {
    super(message);
    this.name = "EnterpriseApiError";
    this.status = status;
    this.operation = options?.operation;
  }
}

export type ReliClarificationSelection = EnterpriseClarificationSelection;
export type ReliPredefinedQuestionId = EnterprisePredefinedQuestionId;
export type ReliActiveClarification = EnterpriseActiveClarification;
export type ReliChatMessage = EnterpriseChatHistoryMessage;
export type ReliConversationSummary = EnterpriseChatConversationSummary;
export type ReliConversationListResponse =
  EnterpriseChatConversationListResponse;
export type ReliConversationHistoryResponse =
  EnterpriseChatConversationHistoryResponse;

const configuredApiBaseUrl = (
  import.meta.env.VITE_ENTERPRISE_API_BASE_URL as string | undefined
)?.replace(/\/$/, "");
const configuredApiPathPrefix = (
  import.meta.env.VITE_ENTERPRISE_API_PATH_PREFIX as string | undefined
)?.trim();
const branchName = (import.meta.env.VITE_BRANCH_NAME as string | undefined)
  ?.toLowerCase()
  .replace(/\//g, "-");
const deployEnvironment =
  (import.meta.env.VITE_DEPLOY_ENVIRONMENT as string | undefined) ?? "dev";

function getDefaultApiBaseUrl() {
  if (deployEnvironment === "prod") {
    return `https://enterprise-app.master.${deployEnvironment}.assetwatch.com`;
  }
  const branch = branchName === "qa" ? "qa" : "dev";
  return `https://enterprise-app.${branch}.${deployEnvironment}.assetwatch.com`;
}

const apiBaseUrl = configuredApiBaseUrl ?? getDefaultApiBaseUrl();
const apiPathPrefix =
  configuredApiPathPrefix === null || configuredApiPathPrefix === undefined
    ? "/enterprise"
    : configuredApiPathPrefix === ""
      ? ""
      : configuredApiPathPrefix.startsWith("/")
        ? configuredApiPathPrefix.replace(/\/$/, "")
        : `/${configuredApiPathPrefix.replace(/\/$/, "")}`;

function getEnterpriseRequestLanguage() {
  const language = i18n.resolvedLanguage ?? i18n.language ?? "en";
  const normalized = language.trim().toLowerCase().split("-")[0];
  return ["en", "es", "ja"].includes(normalized) ? normalized : "en";
}

const authenticatedFetch = createAuthenticatedFetch({
  createRequestId: createEnterpriseRequestId,
  getAdditionalHeaders: () => ({
    "Accept-Language": getEnterpriseRequestLanguage(),
  }),
  onBreadcrumb: addEnterpriseApiBreadcrumb,
});

const client = hc<AppType>(`${apiBaseUrl}${apiPathPrefix}`, {
  fetch: authenticatedFetch,
});

function customerParam(customerId: number) {
  return { customerId };
}

function facilityParam(customerId: number, facilityId: number) {
  return {
    customerId,
    facilityId,
  };
}

export async function fetchEnterpriseReleaseInfo() {
  const res = await client.v1.internal["release-info"].$get();
  const status: number = res.status;

  if (status === 404) {
    return null;
  }

  if (!res.ok) {
    throw new EnterpriseApiError(
      `Enterprise release info request failed with ${status}`,
      status,
      { operation: "release-info" },
    );
  }

  return res.json();
}

export async function fetchEnterpriseCustomerContext(
  externalCustomerId: string,
) {
  const res = await client.v1["customer-context"].$get({
    query: { externalCustomerId },
  });
  if (!res.ok)
    throw new EnterpriseApiError(
      `Enterprise customer lookup failed with ${res.status}`,
      res.status,
      { operation: "customer-context" },
    );
  return res.json();
}

export async function fetchSelectableEnterpriseCustomers() {
  const res = await client.v1.customers.$get();
  if (!res.ok)
    throw new EnterpriseApiError(
      `Enterprise customer list request failed with ${res.status}`,
      res.status,
    );
  return res.json();
}

export async function fetchEnterpriseOverview(customerId: number) {
  const res = await client.v1.customers[":customerId"].overview.$get({
    param: customerParam(customerId),
  });
  if (!res.ok)
    throw new EnterpriseApiError(
      `Enterprise overview request failed with ${res.status}`,
      res.status,
    );
  return res.json();
}

export async function fetchRoiVsSpend(customerId: number) {
  const res = await client.v1.customers[":customerId"]["roi-vs-spend"].$get({
    param: customerParam(customerId),
  });
  if (!res.ok)
    throw new EnterpriseApiError(
      `ROI vs spend request failed with ${res.status}`,
      res.status,
    );
  return res.json();
}

export interface EnterpriseAssetsV2RequestOptions {
  facilityGroupKey?: string;
  facilityId?: string;
  riskType?: "all" | "maintenanceRequired" | "monitoringOffline";
  sortBy?: "daysOpen" | "facilityName" | "groupName";
  sortDirection?: "asc" | "desc";
  page?: number;
  pageSize?: number;
}

export interface EnterpriseAssetsV2SummaryRequestOptions {
  facilityGroupKey?: string;
}

export async function fetchEnterpriseAssetsV2Summary(
  customerId: number,
  options: EnterpriseAssetsV2SummaryRequestOptions = {},
) {
  const res = await client.v1.customers[":customerId"][
    "assets-v2"
  ].summary.$get({
    param: customerParam(customerId),
    query: options,
  });
  if (!res.ok) {
    throw new EnterpriseApiError(
      `Assets V2 summary request failed with ${res.status}`,
      res.status,
      { operation: "assets-v2-summary" },
    );
  }
  return res.json();
}

export async function fetchEnterpriseAssetsV2Table(
  customerId: number,
  options: EnterpriseAssetsV2RequestOptions = {},
) {
  const res = await client.v1.customers[":customerId"]["assets-v2"][
    "high-risk-assets"
  ].$get({
    param: customerParam(customerId),
    query: options,
  });
  if (!res.ok) {
    throw new EnterpriseApiError(
      `Assets V2 table request failed with ${res.status}`,
      res.status,
      { operation: "assets-v2-table" },
    );
  }
  return res.json();
}

export async function fetchHealthScore(customerId: number) {
  const res = await client.v1.customers[":customerId"]["health-score"].$get({
    param: customerParam(customerId),
  });
  if (!res.ok)
    throw new EnterpriseApiError(
      `Health score request failed with ${res.status}`,
      res.status,
    );
  return res.json();
}

export async function fetchFacilityMaturityEvents(
  customerId: number,
  facilityId: number,
) {
  const res = await client.v1.customers[":customerId"].facilities[
    ":facilityId"
  ]["maturity-events"].$get({
    param: facilityParam(customerId, facilityId),
  });
  if (!res.ok) {
    throw new EnterpriseApiError(
      `Facility maturity events request failed with ${res.status}`,
      res.status,
    );
  }
  return res.json();
}

export async function fetchProgramSummary(
  customerId: number,
  durationDays: number,
) {
  const res = await client.v1.customers[":customerId"]["program-summary"].$get({
    param: customerParam(customerId),
    query: { duration: String(durationDays) },
  });
  if (!res.ok) {
    throw new EnterpriseApiError(
      `Program summary request failed with ${res.status}`,
      res.status,
    );
  }
  return res.json();
}

export async function fetchItemsOfConcern(customerId: number) {
  const res = await client.v1.customers[":customerId"]["items-of-concern"].$get(
    {
      param: customerParam(customerId),
    },
  );
  if (!res.ok)
    throw new EnterpriseApiError(
      `Items of concern request failed with ${res.status}`,
      res.status,
    );
  return res.json();
}

export async function fetchRecentWins(customerId: number) {
  const res = await client.v1.customers[":customerId"]["recent-wins"].$get({
    param: customerParam(customerId),
  });
  if (!res.ok)
    throw new EnterpriseApiError(
      `Recent wins request failed with ${res.status}`,
      res.status,
    );
  return res.json();
}

export async function fetchAchievements(customerId: number) {
  const res = await client.v1.customers[":customerId"].achievements.$get({
    param: customerParam(customerId),
  });
  if (!res.ok)
    throw new EnterpriseApiError(
      `Achievements request failed with ${res.status}`,
      res.status,
    );
  return res.json();
}

export async function sendReliMessage(
  customerId: number,
  conversationId: string,
  message: string,
  history: ReliChatMessage[],
  clarificationSelection?: ReliClarificationSelection,
  activeClarification?: ReliActiveClarification,
  signal?: AbortSignal,
  predefinedQuestionId?: ReliPredefinedQuestionId,
) {
  const chatClient = client.v1.customers[":customerId"].chat;
  let res: Awaited<ReturnType<typeof chatClient.$post>>;
  try {
    res = await chatClient.$post(
      {
        param: customerParam(customerId),
        json: {
          conversationId,
          message,
          history,
          clarificationSelection,
          activeClarification,
          predefinedQuestionId,
        },
      },
      { init: { signal } },
    );
  } catch (err) {
    if (!signal?.aborted) {
      captureAskReliError(err, { customerId, phase: "fetch" });
    }
    throw err instanceof Error ? err : new Error(String(err));
  }
  const requestId = res.headers.get("X-Request-Id") ?? undefined;
  if (!res.ok) {
    const responsePreview = await res.text().catch(() => "");
    const err = new EnterpriseApiError(
      `Ask Reli request failed with ${res.status}`,
      res.status,
    );
    captureAskReliError(err, {
      customerId,
      phase: "http_response",
      requestId,
      httpStatus: res.status,
      responsePreview: responsePreview.slice(0, 2000),
    });
    throw err;
  }
  return await readReliResponse(res, customerId, "chat", chatResponseSchema);
}

async function readReliResponse<Schema extends ZodTypeAny>(
  response: Response,
  customerId: number,
  operation: string,
  schema: Schema,
): Promise<ZodOutput<Schema>> {
  const requestId = response.headers.get("X-Request-Id") ?? undefined;
  if (!response.ok) {
    const responsePreview = await response.text().catch(() => "");
    const err = new EnterpriseApiError(
      `Ask Reli ${operation} request failed with ${response.status}`,
      response.status,
    );
    captureAskReliError(err, {
      customerId,
      phase: "http_response",
      requestId,
      httpStatus: response.status,
      responsePreview: responsePreview.slice(0, 2000),
    });
    throw err;
  }

  try {
    const payload = await response.json();
    const parsed = schema.safeParse(payload);
    if (!parsed.success) {
      const err = new EnterpriseApiError(
        `Ask Reli ${operation} response was invalid`,
        502,
        { operation },
      );
      captureAskReliError(err, {
        customerId,
        phase: "validation",
        requestId,
        responsePreview: parsed.error.message.slice(0, 2000),
      });
      throw err;
    }
    return parsed.data;
  } catch (err) {
    if (err instanceof EnterpriseApiError) {
      throw err;
    }
    captureAskReliError(err, { customerId, phase: "json_parse", requestId });
    throw err instanceof Error ? err : new Error(String(err));
  }
}

export async function listReliConversations(
  customerId: number,
  cursor?: string,
): Promise<ReliConversationListResponse> {
  const conversationsClient =
    client.v1.customers[":customerId"].chat.conversations;
  try {
    const response = await conversationsClient.$get({
      param: customerParam(customerId),
      query: { cursor, limit: 50 },
    });
    return await readReliResponse(
      response,
      customerId,
      "conversation list",
      chatConversationListResponseSchema,
    );
  } catch (err) {
    captureAskReliError(err, { customerId, phase: "fetch" });
    throw err instanceof Error ? err : new Error(String(err));
  }
}

export async function listAllReliConversations(
  customerId: number,
): Promise<ReliConversationSummary[]> {
  const conversations: ReliConversationSummary[] = [];
  const seenCursors = new Set<string>();
  let cursor: string | undefined;

  while (true) {
    const page = await listReliConversations(customerId, cursor);
    conversations.push(...page.conversations);

    if (!page.nextCursor) {
      return conversations;
    }
    if (seenCursors.has(page.nextCursor)) {
      throw new Error("Ask Reli returned a repeated conversation cursor.");
    }
    seenCursors.add(page.nextCursor);
    cursor = page.nextCursor;
  }
}

export async function getReliConversation(
  customerId: number,
  conversationId: string,
): Promise<ReliConversationHistoryResponse> {
  const conversationClient =
    client.v1.customers[":customerId"].chat.conversations[":conversationId"];
  try {
    const response = await conversationClient.$get({
      param: { ...customerParam(customerId), conversationId },
    });
    return await readReliResponse(
      response,
      customerId,
      "conversation history",
      chatConversationHistoryResponseSchema,
    );
  } catch (err) {
    captureAskReliError(err, { customerId, phase: "fetch" });
    throw err instanceof Error ? err : new Error(String(err));
  }
}

export function getEnterpriseReliabilityScore(
  response: Awaited<ReturnType<typeof fetchHealthScore>> | undefined | null,
): number | null {
  if (!response) return null;
  const summaryAvg = response.portfolioSummary?.averageOverallScore;
  if (summaryAvg !== null) return summaryAvg;
  const scores =
    response.facilities
      ?.map((f) => f.overallScore)
      .filter((s): s is number => s !== null) ?? [];
  if (scores.length === 0) return null;
  return scores.reduce((a, b) => a + b, 0) / scores.length;
}

// Inferred types from the Hono RPC client

export type EnterpriseSelectableCustomer = Awaited<
  ReturnType<typeof fetchSelectableEnterpriseCustomers>
>[number];
export type EnterpriseCustomerContextResponse = Awaited<
  ReturnType<typeof fetchEnterpriseCustomerContext>
>;
export type EnterpriseOverviewResponse = Awaited<
  ReturnType<typeof fetchEnterpriseOverview>
>;
export type RoiVsSpendResponse = Awaited<ReturnType<typeof fetchRoiVsSpend>>;
export type EnterpriseAssetsV2SummaryResponse = Awaited<
  ReturnType<typeof fetchEnterpriseAssetsV2Summary>
>;
export type EnterpriseAssetsV2TableResponse = Awaited<
  ReturnType<typeof fetchEnterpriseAssetsV2Table>
>;
export type HealthScoreResponse = Awaited<ReturnType<typeof fetchHealthScore>>;
export type FacilityMaturityEventsResponse = Awaited<
  ReturnType<typeof fetchFacilityMaturityEvents>
>;
export type ProgramSummaryResponse = Awaited<
  ReturnType<typeof fetchProgramSummary>
>;
export type ItemsOfConcernResponse = Awaited<
  ReturnType<typeof fetchItemsOfConcern>
>;
export type RecentWinsResponse = Awaited<ReturnType<typeof fetchRecentWins>>;
export type AchievementsResponse = Awaited<
  ReturnType<typeof fetchAchievements>
>;
export type ReliChatResponse = Awaited<ReturnType<typeof sendReliMessage>>;

export type HealthScoreFacility = HealthScoreResponse["facilities"][number];
export type MaturityTier = NonNullable<HealthScoreFacility["maturityTier"]>;
export type PortfolioSummary = HealthScoreResponse["portfolioSummary"];
export type GlobalBenchmarks = HealthScoreResponse["globalBenchmarks"];
export type EnterpriseOverviewFacility =
  EnterpriseOverviewResponse["facilities"][number];
export type RoiVsSpendPoint = RoiVsSpendResponse["series"][number];
export type RoiVsSpendCompliance = RoiVsSpendResponse["compliance"];
export type FacilityMaturityEvent =
  FacilityMaturityEventsResponse["events"][number];
export type ProgramSummaryTopWin =
  ProgramSummaryResponse["value"]["topWins"][number];
export type ItemOfConcern = ItemsOfConcernResponse["items"][number];
export type RecentWin = RecentWinsResponse["wins"][number];
export type AchievementBadge = AchievementsResponse["badges"][number];
export type AchievementTier = AchievementBadge["tiers"][number];
export type AchievementFacility = AchievementTier["facilities"][number];
export type FixItVisualization = NonNullable<ReliChatResponse["visualization"]>;
export type FixItVisualizationPoint = FixItVisualization["points"][number];
export type PortfolioOperationalAverages =
  PortfolioSummary["operationalAverages"];
